import { api } from './index';
import { Methods } from './interfaces';
import { Teacher, User, Child } from '@/types/user';
import { Sale } from '@/types/sale';

const loadTeachers = () => {
  return api<void, Teacher[]>({
    method: Methods.POST,
    route: '/lesson/teachers',
  });
};

const update = (userId: string, options: any) =>
  api<{}, {}>({
    method: Methods.POST,
    route: '/user/update',
    payload: {
      userId,
      ...options,
    },
  });

const getStudentsByEmail = (payload: { email: string }) =>
  api<{ email: string }, { students: User[] }>({
    method: Methods.GET,
    route: '/user/students_by_email',
    payload,
  });

const needContact = () =>
  api<void, void>({
    method: Methods.GET,
    route: '/user/contact',
  });

const getProfile = (userId?: string) => {
  return api<{ userId?: string }, any>({
    method: Methods.GET,
    route: '/user/profile',
    payload: { userId },
  });
};

const getTeacherName = (id: string) =>
  api<{ id?: string }, any>({
    method: Methods.GET,
    route: '/user/teacher/name',
    payload: { id },
  });

const updateProfile = (profile: any, userId?: string) => {
  return api<void, any>({
    method: Methods.POST,
    route: '/user/profile',
    payload: {
      userId,
      ...profile,
    },
  });
};

const updateTeacherParams = (params: any, userId?: string) => {
  return api<void, any>({
    method: Methods.POST,
    route: '/user/teacherParams',
    payload: {
      userId,
      ...params,
    },
  });
};

const checkNickname = (nickname: string) =>
  api<{ nickname: string }, boolean>({
    method: Methods.POST,
    route: '/user/nicktaken',
    payload: {
      nickname,
    },
  });

interface RegisterReq {
  entity: string;
  name?: string;
  password: string;
  age?: number;
  language?: string;
  timezone?: string;
  realName?: string;
  email?: string;
  parentRealName?: string;
  parentEmail?: string;
  parentPhone?: string;
}

interface RegisterRes {
  user: User;
  parent?: User;
}

const register = (payload: RegisterReq) =>
  api<RegisterReq, RegisterRes>({
    method: Methods.POST,
    route: '/user/register-new',
    payload,
  });

const getChildren = (parentEmail?: string) =>
  api<{ parentEmail?: string }, Child[]>({
    method: Methods.GET,
    route: '/user/children',
    payload: { parentEmail },
  });

const createChild = options =>
  api<typeof options, Child>({
    method: Methods.POST,
    route: '/user/children',
    payload: options,
  });

const deleteChild = (childId: string) =>
  api<void, void>({
    method: Methods.DELETE,
    route: `/user/children/${childId}`,
  });

const updateChild = (childId: string, options) =>
  api<typeof options, Child>({
    method: Methods.PUT,
    route: `/user/children/${childId}`,
    payload: options,
  });

const updateChildPassword = (childId: string, password: string) =>
  api<{ password: string }, Child>({
    method: Methods.POST,
    route: `/user/children/${childId}/changePassword`,
    payload: { password },
  });

const linkChild = (username: string, password: string) =>
  api<any, Child[]>({
    method: Methods.POST,
    route: '/user/children/link',
    payload: { username, password },
  });

const getEmployeesUsers = () => {
  return api<void, { users: User[] }>({
    method: Methods.GET,
    route: '/user/employees/',
  });
};

const getMattermostUsers = () => {
  return api<void, any[]>({
    method: Methods.GET,
    route: '/user/mattermost/',
  });
};

const sendMessageToMattermostUsers = (payload: {
  groups: string;
  text: string;
}) => {
  return api<{ groups: string; text: string }, void>({
    method: Methods.POST,
    route: '/user/mattermost/message',
    payload,
  });
};

const getAllEmployeesUsers = () => {
  return api<void, User[]>({
    method: Methods.GET,
    route: '/users/employees/',
  });
};

const confirmParent = (token: string) => {
  return api<void, User[]>({
    method: Methods.POST,
    route: `/user/confirm-parent/${token}`,
  });
};

const resendConfirmParentEmail = () => {
  return api<void, void>({
    method: Methods.GET,
    route: `/user/confirm-parent/email/resend`,
  });
};

const changeConfirmParentEmail = (email: string) => {
  return api<{ email: string }, void>({
    method: Methods.POST,
    route: `/user/confirm-parent/email/change`,
    payload: { email },
  });
};

interface GetEmployeesSalesReq {
  dateFrom: string;
  dateTo: string;
}

interface GetEmployeesSalesRes {
  result: Sale[];
}

const getEmployeesSales = (payload: GetEmployeesSalesReq) => {
  return api<GetEmployeesSalesReq, GetEmployeesSalesRes>({
    method: Methods.GET,
    route: '/user/employees/sales',
    payload,
  });
};

const getCountry = () =>
  api<void, { country: string }>({
    method: Methods.GET,
    route: '/user/country',
  });

const getAccessToken = () =>
  api<void, { token: string }>({
    method: Methods.GET,
    route: '/user/accessToken',
  });

const getInvitationStats = () =>
  api<void, { visits: number; leads: number; sales: number }>({
    method: Methods.GET,
    route: '/user/invitation-stats',
  });

const checkTaxNumber = async (taxNumber: string) =>
  api<{ taxNumber: string }, { status: string }>({
    method: Methods.POST,
    route: '/user/profile/inn',
    payload: { taxNumber },
  });

export const userApi = {
  update,
  loadTeachers,
  getProfile,
  updateProfile,
  updateTeacherParams,
  getStudentsByEmail,
  register,
  getChildren,
  createChild,
  deleteChild,
  updateChild,
  updateChildPassword,
  linkChild,
  checkNickname,
  getEmployeesUsers,
  getMattermostUsers,
  sendMessageToMattermostUsers,
  getAllEmployeesUsers,
  getEmployeesSales,
  confirmParent,
  resendConfirmParentEmail,
  changeConfirmParentEmail,
  getTeacherName,
  getCountry,
  getAccessToken,
  needContact,
  getInvitationStats,
  checkTaxNumber,
};
